import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { TeamPublic } from '@mondy/shared';
import { TeamFlag } from '@/components/TeamFlag';

export function TeamSearch({ teams }: { teams: TeamPublic[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return teams
      .filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.fifaCode.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [query, teams]);

  function select(team: TeamPublic) {
    setQuery('');
    setOpen(false);
    navigate(`/teams/${team.id}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const team = matches[active];
      if (team) select(team);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const showList = open && matches.length > 0;

  return (
    <div style={{ position: 'relative', width: 280, maxWidth: '100%' }}>
      <input
        type="text"
        value={query}
        placeholder="Search a team…"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        // Delay close so a click on an option registers before blur fires.
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        aria-controls="team-search-list"
        style={{
          width: '100%',
          height: 40,
          padding: '0 14px',
          borderRadius: 10,
          border: '1px solid var(--line)',
          background: 'var(--surface)',
          color: 'var(--fg)',
          fontSize: 13,
        }}
      />
      {showList ? (
        <ul
          id="team-search-list"
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            background: 'var(--bg-2)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: '0 12px 28px -10px rgba(0, 0, 0, 0.6)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {matches.map((t, i) => (
            <li key={t.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                // Prevent the input's blur from firing before the click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(t)}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '7px 10px',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                  background:
                    i === active ? 'var(--surface-2)' : 'transparent',
                  color: 'inherit',
                }}
              >
                <TeamFlag
                  code={t.fifaCode}
                  url={t.flagUrl}
                  name={t.name}
                  size="xs"
                />
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: 'var(--fg-muted)',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t.fifaCode}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
